/**
 * Hypoxia warning indicators, from the NAUI Freediver Student Manual (2021),
 * "Safety & Problem Management", pp.27-28.
 *
 * The manual draws a distinction the UI must preserve:
 *   SIGN    - objective, observed by someone else (the safety buddy)
 *   SYMPTOM - subjective, felt only by the diver
 *
 * This matters for dry solo training. A user training alone can only self-assess
 * symptoms; the signs list is what a buddy or spotter watches for. Presenting them
 * as one undifferentiated list would imply a solo trainee can monitor things they
 * cannot actually observe in themselves.
 *
 * Rescue technique is deliberately NOT included here. It requires hands-on
 * instruction, and this app is dry-land training only.
 */

export type IndicatorKind = 'sign' | 'symptom';

export type IndicatorCategory =
  | 'cognitive'
  | 'emotional'
  | 'motor'
  | 'circulatory'
  | 'visual'
  | 'sensory';

export interface HypoxiaIndicator {
  id: string;
  kind: IndicatorKind;
  category: IndicatorCategory;
  label: string;
  description: string;
}

/** Observed by a buddy. Near-blackout signs, per the manual. */
export const NEAR_BLACKOUT_SIGNS: HypoxiaIndicator[] = [
  {
    id: 'sign-confusion',
    kind: 'sign',
    category: 'cognitive',
    label: 'Confusion or faulty judgment',
    description: 'Inability to properly respond to commands, questions or signals.',
  },
  {
    id: 'sign-emotional',
    kind: 'sign',
    category: 'emotional',
    label: 'Emotional instability',
    description: 'Laughing or giggling, or crying and whimpering.',
  },
  {
    id: 'sign-motor',
    kind: 'sign',
    category: 'motor',
    label: 'Loss of motor control',
    description:
      'Hands may shake, or the diver cannot stabilise their head or speak clearly. '
      + 'Lips may quiver, with difficulty breathing from loss of mouth control and '
      + 'diaphragmatic spasms. Still able to keep the airway clear.',
  },
  {
    id: 'sign-cyanosis',
    kind: 'sign',
    category: 'circulatory',
    label: 'Cyanosis',
    description: 'Blueness of the skin, usually the lips and under the eyes.',
  },
  {
    id: 'sign-convulsions',
    kind: 'sign',
    category: 'motor',
    label: 'Convulsions',
    description: 'Muscular convulsions in the legs, arms or whole body.',
  },
];

/** Felt by the diver. These are what a solo dry trainee can self-assess. */
export const NEAR_BLACKOUT_SYMPTOMS: HypoxiaIndicator[] = [
  {
    id: 'symptom-euphoria',
    kind: 'symptom',
    category: 'emotional',
    label: 'Euphoria',
    description:
      'An overwhelming sense of euphoria with uncontrollable laughing, or a sense '
      + 'of dread or despair.',
  },
  {
    id: 'symptom-dizziness',
    kind: 'symptom',
    category: 'cognitive',
    label: 'Dizziness or confusion',
    description:
      'Instructions or questions do not make sense. Hearing may come and go. You may '
      + 'signal that you are fine while compromised.',
  },
  {
    id: 'symptom-tingling',
    kind: 'symptom',
    category: 'sensory',
    label: 'Tingling or numbness',
    description:
      'Tingling or numbness in the legs, arms or face. Usually starts slowly and '
      + 'increases gradually.',
  },
  {
    id: 'symptom-motor',
    kind: 'symptom',
    category: 'motor',
    label: 'Loss of motor control',
    description: 'Inability to control muscle tremors, jerking or shaking.',
  },
  {
    id: 'symptom-visual',
    kind: 'symptom',
    category: 'visual',
    label: 'Visual disturbances',
    description: 'Vision darkens, or peripheral vision collapses into tunnel vision.',
  },
];

/** Full blackout. Same indicators as near-blackout, plus loss of consciousness. */
export const BLACKOUT_SIGNS: HypoxiaIndicator[] = [
  {
    id: 'blackout-motor',
    kind: 'sign',
    category: 'motor',
    label: 'Loss of motor control',
    description:
      'Cannot stabilise the head or upper torso. May go completely limp or become rigid.',
  },
  {
    id: 'blackout-respiratory',
    kind: 'sign',
    category: 'motor',
    label: 'Loss of respiratory control',
    description:
      'Breathing stops for a period. Contractions may occur, or attempts at breathing '
      + 'with no air passing due to laryngospasm.',
  },
  {
    id: 'blackout-cyanosis',
    kind: 'sign',
    category: 'circulatory',
    label: 'Cyanosis',
    description: 'Blueness of the skin, usually the lips and under the eyes.',
  },
  {
    id: 'blackout-convulsions',
    kind: 'sign',
    category: 'motor',
    label: 'Convulsions',
    description: 'Muscular convulsions in the legs, arms or whole body.',
  },
];

/**
 * Abort criteria. Any one of these ends the session — not the round.
 * Presented during a hold, so kept to short scannable phrases.
 */
export const ABORT_TRIGGERS: Array<{ category: string; triggers: string[] }> = [
  { category: 'Visual', triggers: ['Tunnel vision', 'Greying out', 'Flashing lights or stars'] },
  { category: 'Hearing', triggers: ['Ringing or roaring in the ears', 'Hearing fading in and out'] },
  {
    category: 'Neurological',
    triggers: ['Tingling lips, face or extremities', 'Disorientation', 'Sudden calm, warmth or euphoria'],
  },
  { category: 'Motor', triggers: ['Any involuntary twitching or jerking', 'Tremors you cannot control'] },
  { category: 'General', triggers: ['Dizziness', 'Confusion', 'Unusual weakness', 'Feeling unwell'] },
];

/**
 * The single most important physiological point, and the reason comfort is not a
 * safety signal. Shown wherever training intensity is chosen.
 */
export const CONTRACTIONS_ARE_NOT_A_WARNING =
  'Diaphragmatic contractions are a CO2 signal, not an oxygen warning. They are '
  + 'uncomfortable but expected, and they are not a sign you are about to black out. '
  + 'Hypoxia is the dangerous one, and it can arrive with no reliable warning.';

export const AIR_HUNGER_IS_NOT_AN_OXYGEN_GAUGE =
  'The urge to breathe tracks rising CO2, not falling oxygen. Tolerating air hunger '
  + 'better does not make you more resistant to hypoxia — so comfort is never proof '
  + 'that a hold is safe.';

/**
 * NAUI: after ANY blackout or near-blackout, no more diving that day — the diver's
 * oxygen stores are depleted and they are more susceptible to further hypoxic
 * problems. A night's rest is enough to replenish.
 *
 * The stricter 7-day pause below comes from supplementary training material and is
 * kept as the app's conservative default for table work.
 */
export const POST_EVENT_RULES = {
  nauiSameDayStop: {
    rule: 'No further breath-hold training for the rest of the day.',
    citation: 'NAUI Freediver Manual (2021) p.28',
  },
  conservativeTablePause: {
    days: 7,
    rule: 'Pause table training for a full week, then resume at a clearly easier level.',
    citation: 'Freediver Breathing Program, Part I',
  },
  repeatEvents: {
    rule: 'More than one event — see a physician before continuing.',
    citation: 'Freediver Breathing Program, Part I',
  },
} as const;

/**
 * ~90% of freediving blackouts occur after the diver has surfaced and taken a few
 * breaths, which is why the runner enforces a post-hold monitor phase.
 */
export const POST_SURFACE_RISK_NOTE =
  'About 90% of freediving blackouts happen after surfacing, once the diver has '
  + 'already taken a few breaths. Stay alert until breathing is fully normal.';

export const ALL_INDICATORS: HypoxiaIndicator[] = [
  ...NEAR_BLACKOUT_SIGNS,
  ...NEAR_BLACKOUT_SYMPTOMS,
  ...BLACKOUT_SIGNS,
];

export const indicatorsByKind = (kind: IndicatorKind): HypoxiaIndicator[] =>
  ALL_INDICATORS.filter(i => i.kind === kind);
